// ════════════ Web Push: утилиты + подписка ════════════
import { getAccessToken } from './auth';
import { APIRegisterPushSubscription, APICheckPushByToken } from './api';

// ВСТАВЬТЕ публичный VAPID-ключ (тот же, что VAPID_PUBLIC_KEY на сервере)
export const VAPID_PUBLIC_KEY = 'BMtkNGprT3CHSZM_I0-ZssmbY9O0ZcYnka3VJVH2PoUZeMHAj5hx312ZQ1Yk3E0R6QcSfHkvwNNCKp5RIVApphU';

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export const getPlatform = () => {
    const ua = navigator?.userAgent || '';
    const l = ua.toLowerCase();
    const isIOS = /iphone|ipod/.test(l) || /ipad/.test(l) ||
        (navigator.maxTouchPoints > 1 && /macintosh/i.test(ua));
    if (isIOS) return 'ios';
    if (/android/.test(l)) return 'android';
    if (/windows/.test(l)) return 'windows';
    if (/mac/.test(l)) return 'mac';
    return 'unknown';
};

export const getIsStandalone = () =>
    !!(window?.navigator?.standalone ||
       window?.matchMedia('(display-mode: standalone)')?.matches);

const getOrCreateDeviceToken = () => {
    let t = localStorage.getItem('push_device_token');
    if (!t) {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        t = Array.from({ length: 16 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
        localStorage.setItem('push_device_token', t);
    }
    return t;
};

export const isPushSupported = () =>
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const registerSW = async () => {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
};

const sendSubscriptionToServer = async (subscription) => {
    const token = getAccessToken();
    if (!token) return { type: 'no_token' };
    const p256dh = btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh'))));
    const auth = btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))));
    await APIRegisterPushSubscription(token, {
        endpoint: subscription.endpoint,
        p256dh_key: p256dh,
        auth_key: auth,
        browser: navigator.userAgent.slice(0, 200),
        platform: getPlatform(),
        device_token: getOrCreateDeviceToken(),
    });
    return { type: 'success' };
};

// Подписать (если ещё не) и отправить подписку на сервер
export const processPushSubscription = async () => {
    if (!isPushSupported()) return { type: 'not_supported' };
    let reg;
    try { reg = await registerSW(); }
    catch (e) { console.error('SW error:', e); return { type: 'service_worker_error' }; }

    if (getPlatform() === 'ios' && !getIsStandalone()) return { type: 'ios_need_install' };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        if (!VAPID_PUBLIC_KEY || VAPID_PUBLIC_KEY.startsWith('ВСТАВЬТЕ')) {
            console.error('VAPID_PUBLIC_KEY не задан');
            return { type: 'no_vapid_key' };
        }
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
    }
    return await sendSubscriptionToServer(sub);
};

// Запросить разрешение и подписаться (для кнопки в настройках)
export const requestPush = async () => {
    if (!isPushSupported()) return { type: 'not_supported' };
    const platform = getPlatform();
    if (platform === 'ios' && !getIsStandalone()) return { type: 'ios_need_install' };

    const current = Notification.permission;
    if (current === 'granted') return await processPushSubscription();
    if (current === 'denied') return { type: 'permission_denied_permanent' };

    const granted = await Notification.requestPermission();
    if (granted === 'granted') return await processPushSubscription();
    return { type: 'permission_denied' };
};

// Авто-подписка после логина (НЕ спрашивает разрешение — только если уже выдано)
export const ensurePushSubscribed = async () => {
    if (!isPushSupported()) return;
    if (Notification.permission === 'granted') {
        try { await processPushSubscription(); } catch (e) { console.error(e); }
    }
};

// Слушатель сообщений от SW: клик по уведомлению (навигация) + получение пуша
export const setupPushMessageListener = ({ onNavigate, onPush }) => {
    if (!('serviceWorker' in navigator)) return () => {};
    const handler = (event) => {
        if (event.data?.type === 'navigate') onNavigate?.(event.data.url, event.data.notifType);
        if (event.data?.type === 'push_received') onPush?.(event.data.data);
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
};

// Уведомить SW о видимости вкладки
export const notifySWVisibility = (visible) => {
    const sw = navigator.serviceWorker?.controller;
    sw?.postMessage({ type: visible ? 'site_active' : 'site_inactive' });
};

// Бейдж на иконке приложения (Badging API). n>0 — ставим число, иначе снимаем.
// Работает в установленном PWA (Android/десктоп; iOS — с версии 16.4).
export const updateAppBadge = (count) => {
    try {
        if (!('setAppBadge' in navigator)) return;
        const n = Number(count) || 0;
        if (n > 0) navigator.setAppBadge(n).catch(() => {});
        else navigator.clearAppBadge?.().catch(() => {});
    } catch {}
};


// ── Статус подключения push для UI ───────────────────────────────
// 'unsupported' | 'ios_need_install' | 'blocked' | 'not_connected' | 'connected'
export const getPushStatus = async () => {
    if (!isPushSupported()) return 'unsupported';
    if (getPlatform() === 'ios' && !getIsStandalone()) return 'ios_need_install';
    const perm = Notification.permission;
    if (perm === 'denied') return 'blocked';

    let inDb = false;
    try {
        const token = getAccessToken();
        const res = await APICheckPushByToken(token, getOrCreateDeviceToken());
        inDb = !!res?.exists;
    } catch {}

    if (perm === 'granted' && inDb) return 'connected';
    return 'not_connected'; // разрешено, но нет в БД  ИЛИ  ещё не спрашивали
};

// ── Авто-подписка: сначала проверяем БД, потом при необходимости спрашиваем ──
export const autoSubscribeIfNeeded = async () => {
    if (!isPushSupported()) return { type: 'not_supported' };
    if (getPlatform() === 'ios' && !getIsStandalone()) return { type: 'ios_need_install' };

    const token = getAccessToken();
    if (!token) return { type: 'no_token' };

    // 1) Уже есть подписка ЭТОГО устройства в базе?
    let inDb = false;
    try {
        const res = await APICheckPushByToken(token, getOrCreateDeviceToken());
        inDb = !!res?.exists;
    } catch {}

    const perm = Notification.permission;

    // 2) Есть в БД и разрешение выдано — ничего не делаем (только убеждаемся в SW)
    if (inDb && perm === 'granted') {
        try { await registerSW(); } catch {}
        return { type: 'already' };
    }

    // 3) Заблокировано — автоматически не сделать (поможет кнопка в кабинете)
    if (perm === 'denied') return { type: 'permission_denied_permanent' };

    // 4) Иначе — подписываем. requestPush спросит разрешение при 'default'
    //    или тихо подпишет при 'granted'.
    return await requestPush();
};